/**
 * Состояние списка «Мои заказы» живёт в URL (MVP_PRD §20, MVP_TECH_RFC §22).
 *
 * Разбор устроен как в каталоге и результатах: прочитать → провалидировать →
 * **выбросить непонятное**. Мусор из адреса до backend не доезжает: сортировка
 * и стадия там принимаются только из allowlist, и посылать туда произвольную
 * строку — значит получать `422` вместо списка.
 *
 * Раздел архива — тоже часть адреса, а не состояние компонента: ссылка на архив
 * должна открываться архивом, а не активным списком.
 *
 * **Фильтр по стадии сгруппирован по шагам трекера** (FE-200). Стадий двадцать
 * одна, шагов семь, и пользователь думает шагами: «какие заказы ждут оплаты»,
 * а не «какие стоят на `awaiting_registration`». Группировка детерминированная —
 * `stageToStep` из `entities`, — и в API уходит полный список стадий шага, а не
 * его номер: backend фильтрует по колонке `stage`, а не по чужому представлению.
 */

import { z } from 'zod';

import {
  ORDER_STAGES,
  SEARCH_SESSION_SORTS,
  stageToStep,
  type OrderStage,
  type SearchSessionSort,
  type StepNumber,
} from '@entities/search-session';

/** Разделы списка: активные заказы и архив (MVP_PRD §20). */
export const SEARCH_SECTIONS = ['active', 'archived'] as const;
export type SearchSection = (typeof SEARCH_SECTIONS)[number];

/**
 * Значения фильтра стадии: «все» плюс номер шага трекера (MVP_PRD §33.1).
 *
 * Литералы выписаны руками, а не собраны из `TRACKER_STEPS`: `map` даёт
 * `string[]`, тип фильтра схлопывается до `string`, и в URL начинает проходить
 * что угодно. Совпадение с числом шагов проверяет `searchesQuery.test.ts`.
 */
export const STAGE_FILTERS = ['all', '1', '2', '3', '4', '5', '6', '7'] as const;
export type StageFilter = (typeof STAGE_FILTERS)[number];

export const DEFAULT_SEARCH_SORT: SearchSessionSort = 'activity_desc';
export const DEFAULT_SEARCH_SECTION: SearchSection = 'active';
export const DEFAULT_STAGE_FILTER: StageFilter = 'all';

const MAX_PAGE = 1_000;

export interface SearchesQuery {
  section: SearchSection;
  stage: StageFilter;
  sort: SearchSessionSort;
  page: number;
}

const sortSchema = z.enum(SEARCH_SESSION_SORTS);
const sectionSchema = z.enum(SEARCH_SECTIONS);
const stageSchema = z.enum(STAGE_FILTERS);
const pageSchema = z
  .string()
  .regex(/^\d+$/)
  .transform(Number)
  .refine((value) => value >= 1 && value <= MAX_PAGE);

function parseOr<T>(
  schema: z.ZodType<T, z.ZodTypeDef, string>,
  raw: string | null,
  fallback: T,
): T {
  if (raw === null) return fallback;
  const parsed = schema.safeParse(raw);
  return parsed.success ? parsed.data : fallback;
}

export function parseSearchesQuery(params: URLSearchParams): SearchesQuery {
  return {
    section: parseOr(sectionSchema, params.get('section'), DEFAULT_SEARCH_SECTION),
    stage: parseOr(stageSchema, params.get('step'), DEFAULT_STAGE_FILTER),
    sort: parseOr(sortSchema, params.get('sort'), DEFAULT_SEARCH_SORT),
    page: parseOr(pageSchema, params.get('page'), 1),
  };
}

/** Обратная сборка. Значения по умолчанию в адрес не пишутся — он читаемый. */
export function toSearchesParams(query: SearchesQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.section !== DEFAULT_SEARCH_SECTION) params.set('section', query.section);
  if (query.stage !== DEFAULT_STAGE_FILTER) params.set('step', query.stage);
  if (query.sort !== DEFAULT_SEARCH_SORT) params.set('sort', query.sort);
  if (query.page > 1) params.set('page', String(query.page));
  return params;
}

/** Стадии, стоящие на этом шаге трекера. Считается по таблице MVP_PRD §33.2. */
export function stagesOfStep(step: StepNumber): OrderStage[] {
  return ORDER_STAGES.filter((stage) => stageToStep(stage) === step);
}

/** Параметры для API: раздел архива — это фильтр по статусу `archived`. */
export function toSearchesApiQuery(query: SearchesQuery) {
  const step = query.stage === 'all' ? null : (Number(query.stage) as StepNumber);
  return {
    status: query.section === 'archived' ? (['archived'] as const) : undefined,
    stage: step === null ? undefined : stagesOfStep(step),
    sort: query.sort,
    page: query.page,
  };
}
